import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SoundManager from '../../services/SoundManager';
import './CosmicDice.css'; // CSS для анимации кубиков (iOS Safari)

// Утилита для touch на Android WebView
const useTouchClick = (handler: () => void, disabled?: boolean) => {
  const touchedRef = useRef(false);
  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    touchedRef.current = true;
    handler();
    setTimeout(() => { touchedRef.current = false; }, 300);
  }, [disabled, handler]);
  const onClick = useCallback(() => {
    if (disabled || touchedRef.current) { touchedRef.current = false; return; }
    handler();
  }, [disabled, handler]);
  return { onTouchEnd, onClick };
};

interface DiceConfig {
  value: number;
  chance: number;
  multiplier: number;
  duration: number;
  emoji?: string;
}

interface CosmicDiceProps {
  onPlay: (guess?: any) => void;
  onClose: () => void;
  cost: number;
  maxReward: number;
  minReward?: number;
  config?: DiceConfig[]; // Конфиг из API с шансами для каждой суммы
  result?: {
    won: boolean;
    dice1: number;
    dice2: number;
    multiplier?: number;
    duration?: number;
  };
  error?: string;
  isPlaying?: boolean;
  activeBoost?: {
    multiplier: number;
    timeLeft: number;
    expiresAt: string;
  } | null;
}

// Стили инлайн (кроме анимации кубиков - она в CSS файле для iOS)
const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px'
  },
  modal: {
    background: 'linear-gradient(145deg, #1a1625 0%, #252036 100%)',
    borderRadius: '12px',
    padding: '12px',
    width: '100%',
    maxWidth: '300px',
    color: '#fff'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  title: {
    fontSize: '15px',
    fontWeight: 600
  },
  closeBtn: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    color: '#fff',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  boost: {
    background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '12px',
    fontWeight: 600,
    marginBottom: '8px',
    textAlign: 'center' as const
  },
  error: {
    background: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid rgba(239, 68, 68, 0.4)',
    borderRadius: '6px',
    padding: '6px 8px',
    fontSize: '11px',
    color: '#f87171',
    textAlign: 'center' as const,
    marginBottom: '8px'
  },
  diceContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    margin: '12px 0'
  },
  die: {
    width: '50px',
    height: '50px',
    background: '#fff',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    color: '#1a1625',
    boxShadow: '0 3px 10px rgba(0,0,0,0.3)'
  },
  resultBox: {
    textAlign: 'center' as const,
    padding: '8px',
    borderRadius: '8px',
    marginBottom: '8px'
  },
  resultWin: {
    background: 'rgba(34, 197, 94, 0.15)',
    border: '1px solid rgba(34, 197, 94, 0.4)'
  },
  resultLose: {
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)'
  },
  pickerLabel: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.8)',
    textAlign: 'center' as const,
    marginBottom: '6px'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '4px',
    marginBottom: '10px'
  },
  gridBtn: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '6px',
    padding: '6px 2px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    color: '#fff'
  },
  gridBtnActive: {
    background: 'rgba(251, 191, 36, 0.25)',
    borderColor: '#fbbf24'
  },
  playBtn: {
    width: '100%',
    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    border: 'none',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    cursor: 'pointer',
    textTransform: 'uppercase' as const
  },
  info: {
    textAlign: 'center' as const,
    fontSize: '10px',
    color: 'rgba(255,255,255,0.5)',
    marginTop: '8px'
  }
};

const CosmicDice: React.FC<CosmicDiceProps> = ({
  onPlay,
  onClose,
  config,
  result,
  error,
  isPlaying,
  activeBoost
}) => {
  const { t } = useTranslation();
  const [selectedSum, setSelectedSum] = useState<number>(7);
  const [isRolling, setIsRolling] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [dice1, setDice1] = useState(1);
  const [dice2, setDice2] = useState(1);
  const [rollFrame, setRollFrame] = useState(0);

  const faces = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];

  // Строим объект шансов из конфига API или используем дефолты
  const chances: Record<number, number> = {};
  const multipliers: Record<number, number> = {};
  if (config && config.length > 0) {
    config.forEach(item => {
      // Шанс в конфиге - десятичная дробь (0.434), конвертируем в проценты
      chances[item.value] = Math.round(item.chance * 100);
      multipliers[item.value] = item.multiplier;
    });
  } else {
    // Дефолтные значения если конфиг не пришёл
    Object.assign(chances, {
      2: 7, 3: 14, 4: 22, 5: 29, 6: 36,
      7: 43, 8: 36, 9: 29, 10: 22, 11: 14, 12: 7
    });
    Object.assign(multipliers, {
      2: 3.0, 3: 2.5, 4: 2.0, 5: 1.8, 6: 1.5,
      7: 1.5, 8: 1.8, 9: 2.0, 10: 2.5, 11: 3.0, 12: 4.0
    });
  }

  // Вычисляем диапазон множителей
  const multiplierValues = Object.values(multipliers);
  const minMultiplier = Math.min(...multiplierValues);
  const maxMultiplier = Math.max(...multiplierValues);
  const selectedMultiplier = multipliers[selectedSum] || 1.5;

  // Анимация броска - используем isPlaying из props (более надежно для iOS)
  useEffect(() => {
    if (!isPlaying) return;

    // Сразу запускаем анимацию когда isPlaying становится true
    setIsRolling(true);
    setShowResult(false);

    const interval = setInterval(() => {
      setRollFrame(f => f + 1);
      setDice1(Math.floor(Math.random() * 6) + 1);
      setDice2(Math.floor(Math.random() * 6) + 1);
    }, 80);

    return () => {
      clearInterval(interval);
    };
  }, [isPlaying]);

  // Обработка результата - показываем когда пришел result
  useEffect(() => {
    if (result && !isPlaying) {
      // result пришел и isPlaying стал false - показываем результат
      setDice1(result.dice1);
      setDice2(result.dice2);
      setIsRolling(false);
      setShowResult(true);

      if (result.won) {
        SoundManager.playGameWin();
      } else {
        SoundManager.playGameLoss();
      }
    }
  }, [result, isPlaying]);

  const handlePlayAction = useCallback(() => {
    if (isPlaying) return;
    console.log('🎮 CosmicDice play!', selectedSum);
    SoundManager.playButtonClick();
    SoundManager.playGameStart();
    // Разблокируем звуки результата в момент клика (для iOS)
    SoundManager.prepareGameSounds();
    // Сразу начинаем анимацию в контексте user interaction
    setIsRolling(true);
    setShowResult(false);
    onPlay(selectedSum);
  }, [isPlaying, selectedSum, onPlay]);

  const playBtnEvents = useTouchClick(handlePlayAction, isPlaying);

  const handleClose = () => {
    SoundManager.playModalClose();
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>🎲 {t('games.cosmicDice.title')}</span>
          <button style={styles.closeBtn} onClick={handleClose}>×</button>
        </div>

        {/* Active Boost */}
        {activeBoost && activeBoost.timeLeft > 0 && (
          <div style={styles.boost}>
            ⚡ x{activeBoost.multiplier} — {Math.floor(activeBoost.timeLeft / 60)}:{String(activeBoost.timeLeft % 60).padStart(2, '0')}
          </div>
        )}

        {/* Error */}
        {error && <div style={styles.error}>{error}</div>}

        {/* Индикатор броска */}
        {isRolling && (
          <div style={{ textAlign: 'center', color: '#fbbf24', fontSize: '14px', marginBottom: '8px' }}>
            🎲 {t('games.cosmicDice.rolling') || 'Rolling...'}
          </div>
        )}

        {/* Dice - CSS классы из CosmicDice.css для iOS совместимости */}
        <div style={styles.diceContainer}>
          <div className={`cosmic-dice-die ${isRolling ? 'rolling-1' : ''}`}>
            {faces[dice1 - 1]}
          </div>
          <div className={`cosmic-dice-die ${isRolling ? 'rolling-2' : ''}`}>
            {faces[dice2 - 1]}
          </div>
        </div>

        {/* Result */}
        {showResult && result && (
          <div style={{
            ...styles.resultBox,
            ...(result.won ? styles.resultWin : styles.resultLose)
          }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>
              {result.dice1 + result.dice2}
            </div>
            <div style={{
              fontSize: '13px',
              fontWeight: 600,
              color: result.won ? '#4ade80' : '#f87171'
            }}>
              {result.won ? '🎉 ' + t('modals.gameResultModal.victory') : '😔 ' + t('modals.gameResultModal.defeat')}
            </div>
            {result.won && result.multiplier && (
              <div style={{ fontSize: '10px', color: '#fbbf24', marginTop: '2px' }}>
                {t('modals.gameResultModal.boostActivated')}
              </div>
            )}
          </div>
        )}

        {/* Sum Selection */}
        <div style={styles.pickerLabel}>
          {t('games.cosmicDice.selectSum')}: <b style={{ color: '#fbbf24', fontSize: '14px' }}>{selectedSum}</b> ({chances[selectedSum]}%)
        </div>

        <div style={styles.grid}>
          {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
            <button
              key={n}
              style={{
                ...styles.gridBtn,
                ...(selectedSum === n ? styles.gridBtnActive : {})
              }}
              onClick={() => {
                SoundManager.playTabSwitch();
                setSelectedSum(n);
              }}
              disabled={isPlaying}
            >
              <span style={{
                fontSize: '13px',
                fontWeight: 700,
                color: selectedSum === n ? '#fbbf24' : '#fff'
              }}>{n}</span>
              <span style={{ fontSize: '8px', color: 'rgba(255,255,255,0.5)' }}>{chances[n]}%</span>
            </button>
          ))}
        </div>

        {/* Play Button */}
        <button
          className="mobile-touch-btn"
          style={{
            ...styles.playBtn,
            opacity: isPlaying ? 0.6 : 1,
            cursor: isPlaying ? 'not-allowed' : 'pointer',
            touchAction: 'manipulation'
          }}
          onTouchEnd={playBtnEvents.onTouchEnd}
          onClick={playBtnEvents.onClick}
          disabled={isPlaying}
        >
          {isPlaying ? '...' : t('games.cosmicDice.play')}
        </button>

        {/* Info */}
        <div style={styles.info}>
          {t('games.cosmicDice.free')} • x{selectedMultiplier} {t('games.cosmicDice.boostOnWin')}
        </div>
      </div>
    </div>
  );
};

export default CosmicDice;
